/**
 * isLikelyFloorPlan — euristica veloce su File immagine per riconoscere
 * planimetrie/piante catastali e impedire che vengano caricate nella
 * galleria foto dell'annuncio.
 *
 * Segnali combinati (no AI, costo zero):
 *   1. luminanza media molto alta (> 235/255 ≈ "quasi bianco")
 *   2. saturazione bassissima (foto è B/N o quasi)
 *   3. densità di edge alta (linee continue rette, tipiche dei CAD)
 *   4. percentuale di pixel "quasi bianchi" elevata (> 55%)
 *
 * Se 3 su 4 segnali sono positivi → ritorna true.
 *
 * Eseguito su un downsample 128×128 → < 30ms anche su mobile.
 */
export async function isLikelyFloorPlan(file) {
  try {
    const url = URL.createObjectURL(file);
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = url;
    });
    const W = 128;
    const H = 128;
    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, W, H);
    URL.revokeObjectURL(url);

    const { data } = ctx.getImageData(0, 0, W, H);

    let sumLum = 0;
    let sumSat = 0;
    let nearWhite = 0;
    const total = W * H;
    const lum = new Float32Array(total);

    for (let i = 0, p = 0; i < data.length; i += 4, p++) {
      const r = data[i], g = data[i + 1], b = data[i + 2];
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      const l = 0.299 * r + 0.587 * g + 0.114 * b;
      lum[p] = l;
      sumLum += l;
      sumSat += max === 0 ? 0 : (max - min) / max;
      if (r > 220 && g > 220 && b > 220) nearWhite++;
    }

    const avgLum = sumLum / total;
    const avgSat = sumSat / total;
    const whitePct = nearWhite / total;

    // edge density via finite differences (no Sobel needed)
    let edges = 0;
    for (let y = 1; y < H - 1; y++) {
      for (let x = 1; x < W - 1; x++) {
        const idx = y * W + x;
        const gx = Math.abs(lum[idx + 1] - lum[idx - 1]);
        const gy = Math.abs(lum[idx + W] - lum[idx - W]);
        if (gx + gy > 60) edges++;
      }
    }
    const edgeDensity = edges / total;

    const signals = [
      avgLum > 200,        // background dominato dal bianco
      avgSat < 0.10,       // praticamente B/N
      whitePct > 0.55,     // >55% pixel "quasi bianchi"
      edgeDensity > 0.05,  // linee CAD nitide
    ];
    const positives = signals.filter(Boolean).length;
    return positives >= 3;
  } catch {
    // In caso di errore non blocchiamo l'upload — meglio falso negativo.
    return false;
  }
}
